// Base API configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

// Helper to get auth token
const getToken = () => localStorage.getItem("denizns_token");

// Helper to handle API responses
const handleResponse = async (response) => {
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || data.error || "Something went wrong");
    // Preserve the structured error body so callers can act on it — notably the
    // delete-guard flow (409 { requiresConfirmation, data: { assigneeName, ... } }).
    error.status = response.status;
    error.requiresConfirmation = data.requiresConfirmation === true;
    error.info = data.data || null;
    throw error;
  }

  return data;
};

// Helper to append query params
const withQueryParams = (endpoint, params) => {
  if (!params || typeof params !== "object") return endpoint;
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.append(key, String(value));
  });

  const queryString = query.toString();
  if (!queryString) return endpoint;
  return `${endpoint}${endpoint.includes("?") ? "&" : "?"}${queryString}`;
};

// Base fetch wrapper with auth
const apiFetch = async (endpoint, options = {}) => {
  const token = getToken();
  const { params, headers, ...restOptions } = options;
  const finalEndpoint = withQueryParams(endpoint, params);

  const config = {
    ...restOptions,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...headers,
    },
  };

  const response = await fetch(`${API_BASE_URL}${finalEndpoint}`, config);
  return handleResponse(response);
};

// API methods
const api = {
  // GET request
  get: (endpoint, options = {}) =>
    apiFetch(endpoint, { method: "GET", ...options }),

  // POST request
  post: (endpoint, data) =>
    apiFetch(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // PUT request
  put: (endpoint, data) =>
    apiFetch(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  // PATCH request
  patch: (endpoint, data) =>
    apiFetch(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // DELETE request. Accepts optional { params } (e.g. { confirm, taskAction })
  // appended to the query string, since fetch DELETE bodies are unreliable.
  delete: (endpoint, options = {}) =>
    apiFetch(endpoint, { method: "DELETE", ...options }),

  // File upload
  upload: async (endpoint, formData) => {
    const token = getToken();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "POST",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    return handleResponse(response);
  },
};

export default api;
